import { CampaignSDK, SDKConfig, Campaign } from '@core/types';
import { ConfigManager } from '@core/config';
import { createLogger } from '@core/logger';
import { ModificationRegistry } from '@shared/modifications';
import { SelectorEngine } from './SelectorEngine';
import { PageMatcher } from './PageMatcher';
import { SDK_VERSION, DEFAULT_CONFIG, DEFAULT_ENDPOINTS } from '@shared/constants';
import { CampaignError } from '@shared/errors';
import { AnalyticsTracker } from './AnalyticsTracker';
import { normalizePath } from '@shared/utils/dom';

export class RuntimeEngine implements CampaignSDK {
  private config!: ConfigManager;
  private logger = createLogger();
  private modificationRegistry = new ModificationRegistry();
  private selectorEngine = new SelectorEngine();
  private pageMatcher = new PageMatcher();
  private appliedKeys = new Set<string>();
  private isInitialized = false;
  private campaigns: Campaign[] = [];
  private analyticsTracker?: AnalyticsTracker;
  private lastTrackedPath = '';

  constructor() {
    this.setupSpaHooks();
  }

  async initialize(siteKey: string, config: SDKConfig): Promise<void> {
    if (this.isInitialized) {
      this.logger.warn('SDK already initialized');
      return;
    }

    try {
      this.config = new ConfigManager(config, false);
      this.logger = createLogger(this.config.get('debug'));
      this.logger.info('Initializing Campaign SDK Runtime', { version: SDK_VERSION });

      this.campaigns = await this.fetchCampaignData(siteKey);
      const campaignPaths = this.extractCampaignPaths(this.campaigns);
      this.analyticsTracker = new AnalyticsTracker(
        siteKey,
        this.config.get('platformOrigin'),
        campaignPaths,
      );

      this.isInitialized = true;

      if (this.config.get('autoApply')) {
        this.applyModifications();

        // Retry for WooCommerce AJAX loading
        setTimeout(() => this.applyModifications(), 500);
        setTimeout(() => this.applyModifications(), 1000);
        setTimeout(() => this.applyModifications(), 2000);

        // Continuous polling for WooCommerce protection (stop after 10 seconds)
        const pollingInterval = setInterval(() => this.applyModifications(), 200);
        setTimeout(() => clearInterval(pollingInterval), 10000);
      }

      this.logger.info('Campaign SDK Runtime initialized successfully');

      if (config.onLoad) {
        config.onLoad(this);
      }
    } catch (error) {
      this.logger.error('Failed to initialize SDK Runtime', error);
      throw error;
    }
  }

  private async fetchCampaignData(siteKey: string): Promise<Campaign[]> {
    const platformOrigin = DEFAULT_CONFIG.platformOrigin;

    try {
      const endpoint = DEFAULT_ENDPOINTS.campaignData.replace('{siteKey}', siteKey);
      const response = await fetch(`${platformOrigin}${endpoint}`);
      const data = await response.json();

      if (data.ok && data.data && data.data.length > 0) {
        return this.transformCampaignData(data.data);
      } else {
        this.logger.debug('No campaign data available');
        return [];
      }
    } catch (error) {
      this.logger.debug('Failed to fetch campaign data', error);
      return [];
    }
  }

  private transformCampaignData(apiData: any[]): Campaign[] {
    return apiData.map((campaign) => ({
      id: campaign.id,
      query_parameters: campaign.query_parameters || {},
      modifications: campaign.modifications || {},
    }));
  }

  private extractCampaignPaths(campaigns: Campaign[]): string[] {
    const paths = new Set<string>();

    for (const campaign of campaigns) {
      if (campaign.modifications) {
        for (const path of Object.keys(campaign.modifications)) {
          // Remove escaped slashes first, then normalize
          const cleanPath = path.replace(/\\\//g, '/');
          const normalizedPath = normalizePath(
            cleanPath.startsWith('/') ? cleanPath : '/' + cleanPath,
          );
          paths.add(normalizedPath);
        }
      }
    }

    return Array.from(paths);
  }

  applyModifications(): void {
    if (!this.isInitialized) {
      throw new CampaignError('SDK not initialized', 'NOT_INITIALIZED');
    }

    const currentPath = this.pageMatcher.getCurrentPath();

    const revealBody = () => {
      document.body.classList.add('ce-ready');
    };

    if (this.analyticsTracker && this.lastTrackedPath !== currentPath) {
      this.lastTrackedPath = currentPath;
      this.analyticsTracker.trackPageView();
    }

    const activeCampaigns = this.pageMatcher.getActiveCampaigns(this.campaigns);

    if (activeCampaigns.length === 0) {
      revealBody();
      return;
    }

    this.logger.debug(`Applying ${activeCampaigns.length} active campaigns`);

    const urlSearch = window.location.search;
    const selectorCache = new Map<string, Element[]>();

    for (const campaign of activeCampaigns) {
      const runKey = this.pageMatcher.buildRunKey(campaign.id, currentPath, urlSearch);

      if (this.appliedKeys.has(runKey)) {
        this.logger.debug(`Campaign ${campaign.id} already applied to ${currentPath}`);
        continue;
      }

      try {
        const result = this.applyCampaign(campaign, currentPath, selectorCache);

        if (result.anySuccess) {
          this.appliedKeys.add(runKey);
          this.logger.info(`Applied campaign ${campaign.id} to ${currentPath}`);
        }
      } catch (error) {
        this.logger.error(`Failed to apply campaign ${campaign.id}`, error);
      }
    }

    revealBody();
  }

  private applyCampaign(
    campaign: Campaign,
    currentPath: string,
    selectorCache: Map<string, Element[]>,
  ): { failures: number; anySuccess: boolean } {
    const pageMods = campaign.modifications?.[currentPath];

    if (!Array.isArray(pageMods) || pageMods.length === 0) {
      return { failures: 0, anySuccess: false };
    }

    let failures = 0;
    let anySuccess = false;

    for (const modification of pageMods) {
      const result = this.applyModification(modification, selectorCache);
      if (result.failed) {
        failures++;
      } else {
        anySuccess = true;
      }
    }

    return { failures, anySuccess };
  }

  private applyModification(
    modification: any,
    selectorCache: Map<string, Element[]>,
  ): { failed: boolean } {
    if (!modification || !modification.selector || !modification.action) {
      this.logger.warn('Invalid modification', modification);
      return { failed: true };
    }

    const elements = this.getElementsCached(modification.selector, selectorCache);

    if (elements.length === 0) {
      this.logger.debug('Selector matched no elements', modification.selector);
      return { failed: true };
    }

    this.logger.debug(`Applying ${modification.action} to ${elements.length} elements`);

    for (const element of elements) {
      try {
        this.modificationRegistry.apply(modification, element);
      } catch (error) {
        this.logger.error('Failed to apply modification to element', { modification, error });
      }
    }

    return { failed: false };
  }

  private getElementsCached(selector: string, cache: Map<string, Element[]>): Element[] {
    if (cache.has(selector)) {
      return cache.get(selector)!;
    }

    let elements: Element[] = [];
    try {
      elements = Array.from(document.querySelectorAll(selector));
    } catch (error) {
      this.logger.warn('Invalid selector', { selector, error });
    }

    cache.set(selector, elements);
    return elements;
  }

  private setupSpaHooks(): void {
    try {
      const push = history.pushState;
      const replace = history.replaceState;

      history.pushState = function (data: any, unused: string, url?: string | URL | null) {
        push.call(this, data, unused, url);
        window.dispatchEvent(new Event('locationchange'));
      };

      history.replaceState = function (data: any, unused: string, url?: string | URL | null) {
        replace.call(this, data, unused, url);
        window.dispatchEvent(new Event('locationchange'));
      };

      window.addEventListener('popstate', () => {
        window.dispatchEvent(new Event('locationchange'));
      });

      window.addEventListener('locationchange', () => {
        if (this.isInitialized) {
          this.applyModifications();
        }
      });

      window.addEventListener('hashchange', () => {
        if (this.isInitialized) {
          this.applyModifications();
        }
      });
    } catch (error) {
      this.logger.warn('Failed to setup SPA hooks', error);
    }
  }

  destroy(): void {
    this.appliedKeys.clear();
    this.selectorEngine.clearCache();
    this.isInitialized = false;
    this.logger.info('Campaign SDK destroyed');
  }

  getVersion(): string {
    return SDK_VERSION;
  }

  isDebug(): boolean {
    return this.config?.get('debug') ?? false;
  }
}
